import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ArrowUp, Loader2, MessageCircle, Plus, Search, Trash2 } from 'lucide-react';
import ChatMarkdown from './ChatMarkdown';
import {
  apiService,
  ExplorationModel,
  ExplorationSource,
  ExplorationConversationSummary,
} from '../services/api';
import DocumentUploadBar from './DocumentUploadBar';

type ExplorationMode = 'chat' | 'search';

interface ExplorationMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  mode?: ExplorationMode;
  modelLabel?: string;
  sources?: ExplorationSource[];
}

interface ExplorationPanelProps {
  renderSidebar?: (sidebar: ReactNode) => ReactNode;
}

const ExplorationMarkdown: React.FC<{ text: string }> = ({ text }) => (
  <ChatMarkdown text={text} className="structured-markdown" />
);

const hostnameForUrl = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch (err) {
    return url;
  }
};

/** Compact "host/path" label so multiple links to one site stay distinguishable. */
const shortLabelForUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, '');
    const path = parsed.pathname.replace(/\/+$/, '');
    if (!path || path === '/') return host;
    const trimmedPath = path.length > 26 ? `${path.slice(0, 26)}…` : path;
    return `${host}${trimmedPath}`;
  } catch (err) {
    return url;
  }
};

/**
 * Search replies end with a markdown "Sources:" list per the system prompt.
 * Those URLs are surfaced as chips and in the sidebar, so hide the raw block
 * from the message body to avoid showing the same links twice.
 */
const stripTrailingSourcesBlock = (text: string): string => {
  if (!text) return text;
  const lines = text.split('\n');
  let headerIdx = -1;
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    if (/^\s*(?:#{1,6}\s*)?(?:\*\*)?\s*Sources:?\s*(?:\*\*)?\s*$/i.test(lines[i])) {
      headerIdx = i;
      break;
    }
  }
  if (headerIdx === -1) return text;
  const tail = lines.slice(headerIdx + 1);
  const isOnlySourceLines = tail.every(
    (line) =>
      !line.trim() ||
      /https?:\/\//.test(line) ||
      /^\s*(?:[-*]|\d+[.)])\s/.test(line)
  );
  if (!isOnlySourceLines) return text;
  return lines.slice(0, headerIdx).join('\n').trimEnd();
};

const ExplorationPanel: React.FC<ExplorationPanelProps> = ({ renderSidebar }) => {
  const [models, setModels] = useState<ExplorationModel[]>([]);
  const [selectedNick, setSelectedNick] = useState<string>('');
  const [mode, setMode] = useState<ExplorationMode>('chat');
  const [messages, setMessages] = useState<ExplorationMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docsBusy, setDocsBusy] = useState(false);
  const [explorationSessionId, setExplorationSessionId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<ExplorationConversationSummary[]>([]);
  const [loadingConversationId, setLoadingConversationId] = useState<string | null>(null);
  // Hide a mode button's tooltip after it is clicked, until the cursor leaves.
  const [suppressedTip, setSuppressedTip] = useState<ExplorationMode | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const sessionPromiseRef = useRef<Promise<string> | null>(null);
  const explorationSessionIdRef = useRef<string | null>(null);
  const stickToBottomRef = useRef(true);
  const pendingScrollFrameRef = useRef<number | null>(null);
  explorationSessionIdRef.current = explorationSessionId;

  const scheduleScrollToBottom = useCallback((force = false) => {
    if (pendingScrollFrameRef.current !== null) {
      return;
    }
    pendingScrollFrameRef.current = window.requestAnimationFrame(() => {
      pendingScrollFrameRef.current = null;
      const container = scrollRef.current;
      if (!container) return;
      if (!force && !stickToBottomRef.current) return;
      container.scrollTop = container.scrollHeight;
    });
  }, []);

  const handleListScroll: React.UIEventHandler<HTMLDivElement> = useCallback((event) => {
    const container = event.currentTarget;
    const distanceFromBottom =
      container.scrollHeight - (container.scrollTop + container.clientHeight);
    stickToBottomRef.current = distanceFromBottom <= 48;
  }, []);

  const refreshConversations = useCallback(async () => {
    try {
      const list = await apiService.listExplorationConversations();
      setConversations(list);
    } catch {
      // Non-fatal: history list is best-effort.
    }
  }, []);

  useEffect(() => {
    void refreshConversations();
  }, [refreshConversations]);

  // Documents are scoped to a session; exploration creates one lazily on the
  // first upload (any backend instance can then read them from Postgres).
  const ensureExplorationSession = useCallback(async (): Promise<string> => {
    if (explorationSessionIdRef.current) return explorationSessionIdRef.current;
    if (sessionPromiseRef.current) return sessionPromiseRef.current;
    const creation = (async () => {
      const response = await apiService.initializeSession({ source: 'exploration' });
      setExplorationSessionId(response.session_id);
      return response.session_id;
    })();
    sessionPromiseRef.current = creation;
    try {
      return await creation;
    } finally {
      sessionPromiseRef.current = null;
    }
  }, []);

  const startNewConversation = useCallback(() => {
    setMessages([]);
    setExplorationSessionId(null);
    sessionPromiseRef.current = null;
    setError(null);
  }, []);

  const openConversation = useCallback(
    async (sessionId: string) => {
      if (sending || loadingConversationId) return;
      setLoadingConversationId(sessionId);
      setError(null);
      try {
        const data = await apiService.getExplorationConversation(sessionId);
        setExplorationSessionId(sessionId);
        setMessages(
          (data.messages || []).map((msg) => ({
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp,
            mode: msg.mode,
            modelLabel: msg.model_label,
            sources: msg.sources,
          }))
        );
        if (data.model_nick) {
          setSelectedNick((prev) =>
            models.some((m) => m.nick === data.model_nick) ? (data.model_nick as string) : prev
          );
        }
      } catch {
        setError('Unable to load that conversation.');
      } finally {
        setLoadingConversationId(null);
      }
    },
    [sending, loadingConversationId, models]
  );

  const deleteConversation = useCallback(
    async (sessionId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      try {
        await apiService.deleteExplorationConversation(sessionId);
        setConversations((prev) => prev.filter((c) => c.session_id !== sessionId));
        if (explorationSessionIdRef.current === sessionId) {
          startNewConversation();
        }
      } catch {
        setError('Unable to delete that conversation.');
      }
    },
    [startNewConversation]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await apiService.fetchExplorationModels();
        if (cancelled) return;
        setModels(list);
        if (list.length) {
          setSelectedNick((prev) => prev || list[0].nick);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Unable to load models. Please refresh and try again.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedModel = useMemo(
    () => models.find((m) => m.nick === selectedNick) || null,
    [models, selectedNick]
  );

  const modelTierGroups = useMemo(() => {
    const tierLabels: Array<{ tier: string; label: string }> = [
      { tier: 'light', label: 'Light' },
      { tier: 'medium', label: 'Medium' },
      { tier: 'premium', label: 'Premium' },
    ];
    return tierLabels
      .map(({ tier, label }) => ({
        label,
        // Models without a tier (older backend) default to Medium.
        models: models.filter((m) => (m.tier || 'medium') === tier),
      }))
      .filter((group) => group.models.length > 0);
  }, [models]);

  useLayoutEffect(() => {
    // New message rows / typing indicator: always pin to bottom.
    stickToBottomRef.current = true;
    scheduleScrollToBottom(true);
  }, [messages.length, sending, scheduleScrollToBottom]);

  useEffect(
    () => () => {
      if (pendingScrollFrameRef.current !== null) {
        window.cancelAnimationFrame(pendingScrollFrameRef.current);
        pendingScrollFrameRef.current = null;
      }
    },
    []
  );

  const resizeInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const nextHeight = Math.min(Math.max(el.scrollHeight, 40), 140);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > 140 ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    resizeInput();
  }, [inputValue, resizeInput]);

  const sendMessage = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || sending || !selectedNick) return;

    setSending(true);
    setError(null);
    setInputValue('');

    const timestamp = new Date().toISOString();
    const history = messages.map((msg) => ({ role: msg.role, content: msg.content }));
    const modelLabel = selectedModel?.label || selectedNick;
    let assistantIndex = -1;

    setMessages((prev) => {
      assistantIndex = prev.length + 1;
      return [
        ...prev,
        { role: 'user', content: trimmed, timestamp },
        {
          role: 'assistant',
          content: '',
          timestamp: `${timestamp}-assistant`,
          mode,
          modelLabel,
        },
      ];
    });

    const applyToken = (chunk: string) => {
      if (!chunk) return;
      setMessages((prev) =>
        prev.map((msg, idx) =>
          idx === assistantIndex ? { ...msg, content: `${msg.content || ''}${chunk}` } : msg
        )
      );
      // Content height grows during stream without changing messages.length;
      // pin to bottom on each token (same pattern as ChatPanel).
      scheduleScrollToBottom(false);
    };

    try {
      // A session id is required so the exchange is persisted (and documents
      // stay scoped to this conversation). Created lazily on first message.
      let resolvedSessionId: string | null = explorationSessionId;
      try {
        resolvedSessionId = await ensureExplorationSession();
      } catch {
        resolvedSessionId = explorationSessionId;
      }
      const result = await apiService.streamExplorationMessage(trimmed, {
        modelNick: selectedNick,
        mode,
        history,
        sessionId: resolvedSessionId,
        onToken: applyToken,
      });
      setMessages((prev) =>
        prev.map((msg, idx) =>
          idx === assistantIndex
            ? {
                ...msg,
                content: result.reply || msg.content,
                sources: result.sources,
              }
            : msg
        )
      );
      void refreshConversations();
    } catch (err) {
      console.error('Exploration chat failed', err);
      setMessages((prev) => prev.slice(0, Math.max(0, prev.length - 2)));
      setInputValue(trimmed);
      const status = (err as { status?: number })?.status;
      setError(
        status === 402
          ? 'You are out of credits. Please add credits to continue using Exploration mode.'
          : 'Unable to reach the model. Please try again.'
      );
    } finally {
      setSending(false);
    }
  }, [
    inputValue,
    sending,
    selectedNick,
    selectedModel,
    messages,
    mode,
    explorationSessionId,
    ensureExplorationSession,
    refreshConversations,
    scheduleScrollToBottom,
  ]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void sendMessage();
    }
  };

  const latestSources = useMemo(() => {
    for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
      const msg = messages[idx];
      if (msg.role === 'assistant' && msg.sources && msg.sources.length) {
        return msg.sources;
      }
    }
    return [];
  }, [messages]);

  const sourcesSidebar =
    latestSources.length > 0 ? (
      <div className="explore-sources-card">
        <h3 className="explore-sources-card__title">Sources</h3>
        <ul className="explore-sources-card__list">
          {latestSources.map((src, idx) => (
            <li key={`${src.url}-${idx}`}>
              <a href={src.url} target="_blank" rel="noreferrer noopener">
                {src.title || hostnameForUrl(src.url)}
                <span className="explore-sources-card__url">
                  {src.url.replace(/^https?:\/\/(www\.)?/, '')}
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  const historySidebar =
    conversations.length > 0 ? (
      <div className="explore-sources-card explore-history-card">
        <h3 className="explore-sources-card__title">Past explorations</h3>
        <ul className="explore-history-list">
          {conversations.map((conv) => (
            <li
              key={conv.session_id}
              className={`explore-history-item${
                conv.session_id === explorationSessionId ? ' explore-history-item--active' : ''
              }`}
              onClick={() => void openConversation(conv.session_id)}
              title={conv.title}
            >
              {loadingConversationId === conv.session_id ? (
                <Loader2 size={12} className="brief-spinner" />
              ) : (
                <MessageCircle size={12} />
              )}
              <span className="explore-history-item__title">{conv.title}</span>
              <button
                type="button"
                className="explore-history-item__delete"
                onClick={(event) => void deleteConversation(conv.session_id, event)}
                title="Delete conversation"
              >
                <Trash2 size={12} />
              </button>
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  const sidebarContent =
    sourcesSidebar || historySidebar ? (
      <>
        {sourcesSidebar}
        {historySidebar}
      </>
    ) : null;

  return (
    <>
      {renderSidebar && sidebarContent ? renderSidebar(sidebarContent) : null}
      <div className="explore-panel">
        <div className="explore-header">
          <div>
            <span className="explore-header__eyebrow">Exploration Mode</span>
            <h1 className="explore-header__title">Astra Exploration</h1>
          </div>
          <div className="explore-header__controls">
            {messages.length > 0 && (
              <button
                type="button"
                className="explore-new-chat"
                onClick={startNewConversation}
                disabled={sending}
                title="Start a new conversation"
              >
                <Plus size={13} />
                New chat
              </button>
            )}
            <div className="explore-model-pill" title={selectedModel?.purpose || 'Active model'}>
              <span className="explore-model-pill__dot" />
              <select
                aria-label="AI model"
                value={selectedNick}
                onChange={(event) => setSelectedNick(event.target.value)}
                disabled={sending}
              >
                {modelTierGroups.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.models.map((m) => (
                      <option key={m.nick} value={m.nick}>
                        {m.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>
        </div>

        <DocumentUploadBar
          sessionId={explorationSessionId}
          ensureSessionId={ensureExplorationSession}
          onBusyChange={setDocsBusy}
        />

        <div className="explore-scroll" ref={scrollRef} onScroll={handleListScroll}>
          {messages.length === 0 && (
            <div className="brief-thread__placeholder">
              Pick a model, then send a message. Switch to Search when you want
              web-grounded answers with sources.
            </div>
          )}
          {messages.map((msg, idx) => {
            if (msg.role === 'user') {
              return (
                <div
                  key={`${msg.timestamp}-${idx}`}
                  className="brief-message brief-message--user brief-message--plain"
                >
                  <div className="brief-message__label">You</div>
                  <div className="brief-message__content">{msg.content}</div>
                </div>
              );
            }
            return (
              <div key={`${msg.timestamp}-${idx}`} className="explore-msg">
                <div className="explore-msg__meta">
                  {msg.modelLabel || 'Assistant'}
                  {msg.mode === 'search' && (
                    <span className="explore-msg__badge">
                      <Search size={9} strokeWidth={2.5} />
                      Search
                    </span>
                  )}
                </div>
                <div className="explore-msg__bubble">
                  <ExplorationMarkdown
                    text={msg.mode === 'search' ? stripTrailingSourcesBlock(msg.content) : msg.content}
                  />
                  {msg.sources && msg.sources.length > 0 && (
                    <div className="explore-msg__source-chips">
                      <span className="explore-msg__source-chips-label">Sources</span>
                      {msg.sources.map((src, srcIdx) => (
                        <a
                          key={`${src.url}-${srcIdx}`}
                          className="explore-src-chip"
                          href={src.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          title={src.url}
                        >
                          <span className="explore-src-chip__n">{srcIdx + 1}</span>
                          {shortLabelForUrl(src.url)}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {sending && (
            <div className="brief-message brief-message--assistant brief-message--typing brief-message--plain">
              <Loader2 className="brief-spinner" />
              <span>{mode === 'search' ? 'Searching...' : 'Thinking...'}</span>
            </div>
          )}
        </div>

        {error && <div className="brief-error">{error}</div>}

        <div className="explore-composer">
          <textarea
            ref={inputRef}
            rows={1}
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              docsBusy
                ? 'Reading document… chat resumes when it’s ready'
                : mode === 'search'
                  ? 'Ask something to search and answer…'
                  : 'Ask anything…'
            }
            disabled={sending || docsBusy}
          />
          <div className="explore-composer__bar">
            <div className="explore-seg-toggle" role="tablist" aria-label="Mode">
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'chat'}
                className={mode === 'chat' ? 'active' : ''}
                onClick={() => {
                  setMode('chat');
                  setSuppressedTip('chat');
                }}
                onMouseLeave={() => setSuppressedTip(null)}
              >
                <MessageCircle size={11} strokeWidth={2.2} />
                Chat
                {suppressedTip !== 'chat' && (
                  <span className="explore-mode-tip" role="tooltip">
                    <strong>Chat</strong>
                    Answers from the model&apos;s own knowledge. It searches the
                    web only when it decides it needs current information.
                  </span>
                )}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'search'}
                className={mode === 'search' ? 'active' : ''}
                onClick={() => {
                  setMode('search');
                  setSuppressedTip('search');
                }}
                onMouseLeave={() => setSuppressedTip(null)}
              >
                <Search size={11} strokeWidth={2.2} />
                Search
                {suppressedTip !== 'search' && (
                  <span className="explore-mode-tip" role="tooltip">
                    <strong>Search</strong>
                    Always searches the web first, then builds the answer from
                    the results and lists its sources.
                  </span>
                )}
              </button>
            </div>
            <button
              type="button"
              className="explore-send"
              title="Send"
              onClick={() => void sendMessage()}
              disabled={sending || docsBusy || !inputValue.trim() || !selectedNick}
            >
              <ArrowUp size={15} strokeWidth={2.4} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExplorationPanel;
